import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Chip, Grid, Skeleton, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { BentoCategory } from "schemas/dashboard";
import { Map } from "schemas/functions";
import { CustomEvent } from "schemas/functions";

import { fetcherAuth } from "utils/api";
import { getSource } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

import styles from "./styles";

interface Props {
  handleBack: () => void;
  handleSkip?: () => void;
  onboardingForm: Map;
  moveNext?: () => void;
  isVaOnboarding?: boolean;
  setNeedsUpdateCategory?: React.Dispatch<React.SetStateAction<boolean>>;
}

const generateWidths = (count: number, min = 85, max = 200) => {
  return Array.from(
    { length: count },
    () => `${Math.floor(Math.random() * (max - min + 1)) + min}px`,
  );
};

const CategoryForm = ({
  handleBack,
  handleSkip,
  moveNext,
  isVaOnboarding = false,
  setNeedsUpdateCategory,
}: Props) => {
  const { referralCode } = useContext(SubscriptionContext);
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg, setCurrentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const location = useLocation();
  const source = getSource();

  const [selectedCategories, setSelectedCategories] = useState<BentoCategory[]>(
    currentOrg?.bentoCategories || [],
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [categoriesToPickFrom, setCategoriesToPickFrom] = useState<
    BentoCategory[]
  >([]);

  const handleSubmit = async (event: CustomEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      const { categories } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/categories`,
        "PUT",
        {},
        {
          categoryIds: selectedCategories?.map((x) => x.id),
          page: location.pathname,
          source: referralCode ? "referral" : source,
          isVaFlow: isVaOnboarding,
          isOnboarding: true,
        },
      );

      setCurrentOrg((prev) => {
        const copy = makeDeepCopy(prev);
        copy.bentoCategories = categories;
        return copy;
      });

      if (moveNext) await moveNext();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoriesToPickFrom = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-categories/onboarding`,
      );
      setCategoriesToPickFrom(res.onboardingBentoCategories);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const isSelected = (chip: BentoCategory) => {
    return selectedCategories?.some((x: BentoCategory) => x.id === chip.id);
  };

  const addCategory = (copy: BentoCategory[], category: BentoCategory) => {
    const index = copy.findIndex((x: BentoCategory) => x.id === category.id);
    if (index > -1) {
      copy.splice(index, 1);
    } else {
      copy.push(category);
    }
    return copy;
  };

  const selectChip = (chip: BentoCategory) => {
    if (setNeedsUpdateCategory) setNeedsUpdateCategory(true);
    setSelectedCategories((prev) => {
      const copy = [...prev];
      addCategory(copy, chip);
      return copy;
    });
  };

  useEffect(() => {
    if (currentOrg?.id) {
      getCategoriesToPickFrom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const loadingElements = useMemo(
    () => generateWidths(23),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    setSelectedCategories(currentOrg?.bentoCategories || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.bentoCategories?.length]);

  return (
    <form onSubmit={handleSubmit}>
      <Typography variant="h3" gutterBottom sx={{ textAlign: "center" }}>
        <Box
          component="i"
          color="secondary.dark"
          sx={{ pr: 2 }}
          className="fa-solid fa-layer-group"
        />
        Your Brand Types
      </Typography>
      <Typography
        variant="subtitle1"
        gutterBottom
        sx={{ textAlign: "center", mb: 3 }}
      >
        What are your preferred types of brands to work with? *
      </Typography>
      <Grid container justifyContent="center" sx={{ my: 2 }} gap={2}>
        {categoriesToPickFrom.length === 0 &&
          loadingElements.map((width, i) => (
            <Skeleton
              key={i}
              variant="rounded"
              width={width}
              sx={styles.loadingSkeleton}
            />
          ))}
        {categoriesToPickFrom?.map((category, index) => (
          <Chip
            key={index}
            sx={[isSelected(category) ? styles.selected : {}]}
            label={category.name}
            onClick={() => selectChip(category)}
          />
        ))}
      </Grid>
      <Grid item xs="auto" container columnGap={1}>
        <Grid item xs="auto">
          <Button onClick={handleBack} color="secondary" disableElevation>
            Back
          </Button>
        </Grid>
        <Grid item xs></Grid>
        {handleSkip && (
          <Grid item xs="auto">
            <Button onClick={handleSkip} color="secondary" disableElevation>
              Skip
            </Button>
          </Grid>
        )}
        <Grid item xs="auto">
          <LoadingButton
            loading={loading}
            disableElevation
            variant="contained"
            type="submit"
            disabled={selectedCategories.length === 0}
          >
            Next
          </LoadingButton>
        </Grid>
      </Grid>
    </form>
  );
};

export default CategoryForm;
